import type { MerchantApiClient } from "../../types.js";
import { dispatchMerchantTelemetry } from "../../shared/telemetry.js";
import { createCompetitorsApi, type CompetitorsApi } from "./api.js";
import type {
  CompetitorDomain,
  CompetitorFilters,
  CompetitorFormDraft,
  CompetitorPage,
  CompetitorTestResult,
} from "./models.js";

export interface CompetitorsTelemetry {
  track(eventName: string, payload: Record<string, string | number | boolean | undefined>): void;
}

export interface CompetitorsService {
  createCompetitor(draft: CompetitorFormDraft): Promise<CompetitorDomain>;
  deleteCompetitor(competitorId: string): Promise<CompetitorDomain>;
  loadCompetitor(competitorId: string): Promise<CompetitorDomain>;
  loadCompetitors(filters: CompetitorFilters): Promise<CompetitorPage>;
  testCompetitor(competitorId: string): Promise<CompetitorTestResult>;
  updateCompetitor(competitorId: string, draft: CompetitorFormDraft): Promise<CompetitorDomain>;
}

export interface CompetitorsServiceOptions {
  api?: CompetitorsApi;
  apiClient?: MerchantApiClient;
  idempotencyKeyFactory?: () => string;
  telemetry?: CompetitorsTelemetry;
}

export function createCompetitorsService(options: CompetitorsServiceOptions): CompetitorsService {
  const api = options.api ?? createApiFromClient(options.apiClient);
  const telemetry = options.telemetry ?? browserTelemetry();
  const idempotencyKeyFactory = options.idempotencyKeyFactory ?? (() => crypto.randomUUID());

  return {
    createCompetitor(draft) {
      return track(
        telemetry,
        "merchant_competitor_created",
        () => api.createCompetitor(draft, idempotencyKeyFactory()),
        { domain: draft.domain },
      );
    },
    deleteCompetitor(competitorId) {
      return track(
        telemetry,
        "merchant_competitor_deleted",
        () => api.deleteCompetitor(competitorId, idempotencyKeyFactory()),
        { competitorId },
      );
    },
    loadCompetitor(competitorId) {
      return track(
        telemetry,
        "merchant_competitor_detail_loaded",
        () => api.getCompetitor(competitorId),
        { competitorId },
      );
    },
    loadCompetitors(filters) {
      return track(telemetry, "merchant_competitors_loaded", () => api.listCompetitors(filters), {
        competitorStatus: filters.competitorStatus,
        search: filters.search,
      });
    },
    testCompetitor(competitorId) {
      return track(
        telemetry,
        "merchant_competitor_tested",
        () => api.testCompetitor(competitorId, idempotencyKeyFactory()),
        { competitorId },
      );
    },
    updateCompetitor(competitorId, draft) {
      return track(
        telemetry,
        "merchant_competitor_updated",
        () => api.updateCompetitor(competitorId, draft, idempotencyKeyFactory()),
        { competitorId, domain: draft.domain },
      );
    },
  };
}

async function track<T>(
  telemetry: CompetitorsTelemetry,
  eventName: string,
  operation: () => Promise<T>,
  payload: Record<string, string | number | boolean | undefined>,
): Promise<T> {
  const startedAt = performance.now();
  try {
    const result = await operation();
    telemetry.track(eventName, {
      ...payload,
      result: "success",
      durationMs: Math.round(performance.now() - startedAt),
    });
    return result;
  } catch (error: unknown) {
    telemetry.track(eventName, {
      ...payload,
      result: "failure",
      durationMs: Math.round(performance.now() - startedAt),
      errorName: error instanceof Error ? error.name : "unknown",
    });
    throw error;
  }
}

function createApiFromClient(client: MerchantApiClient | undefined): CompetitorsApi {
  if (client === undefined) {
    throw new Error("Competitors service requires an API client.");
  }

  return createCompetitorsApi(client);
}

function browserTelemetry(): CompetitorsTelemetry {
  return {
    track(eventName, payload) {
      dispatchMerchantTelemetry(eventName, payload);
    },
  };
}
